'use client';

import React, { forwardRef, type ReactNode } from 'react';
import { cn } from '@/components/ui/_lib/cn';
import {
  buttonSurfaceClass,
  type ButtonVariantProp,
} from '@/components/ui/_lib/buttonVariants';
import type { UiSkeletonProps } from '@/components/ui/_lib/skeletonProps';
import { useDeferredSkeleton } from '@/components/ui/_lib/useDeferredSkeleton';
import { SkeletonLines } from '@/components/ui/_lib/SkeletonLines';
import { SKELETON_BY_COMPONENT } from '@/components/ui/_lib/skeletonRegistry';

const sk = SKELETON_BY_COMPONENT.Button;

export type ButtonProps = {
  children?: ReactNode;
  variant?: ButtonVariantProp;
} & React.ButtonHTMLAttributes<HTMLButtonElement> &
  UiSkeletonProps;

const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    skeleton = false,
    skeletonMaxWaitMs = 2000,
    skeletonLines,
    className,
    children,
    variant = true,
    ...rest
  } = props;

  const showSkeleton = useDeferredSkeleton(skeleton, skeletonMaxWaitMs);

  return (
    <button ref={ref} className={cn('group', buttonSurfaceClass(variant, className))} {...rest}>
      <SkeletonLines show={showSkeleton} shape={sk.shape} lines={skeletonLines ?? sk.lines}>
        {children}
      </SkeletonLines>
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
